import { inject, Injectable, signal } from '@angular/core';
import { QueryDataSource } from '@softapps/core';
import {
  ApiDataAccess,
  HttpApiQueryRepository,
  interpolate,
  Query,
  QueryResult,
} from '@softline/core';
import { statusFilterAsCodes } from '../helper/status-filter.function';
import { SOFTLINE_API_DELIVERY_NOTE_QUERY } from '../delivery-note-correction.api';
import { CommissioningStagesService } from '../services/commissioning-stages.service';
import { DocumentIconsService } from '../services/document-icons.service';
import { DeliveryNoteQueryRow } from '../types/delivery-note-query-row';
import { isChildPageQuery, isRowPageQuery } from './row-page-query.function';

@Injectable()
export class DeliveryNoteQueryDataSource
  extends HttpApiQueryRepository<DeliveryNoteQueryRow>
  implements QueryDataSource<DeliveryNoteQueryRow>
{
  type = 'query' as const;

  private readonly iconsService = inject(DocumentIconsService);
  private readonly stagesService = inject(CommissioningStagesService);

  /** `lastRow` of the latest row page; null until the first page answered. */
  readonly total = signal<number | null>(null);

  constructor() {
    super(
      inject(ApiDataAccess),
      interpolate(SOFTLINE_API_DELIVERY_NOTE_QUERY, { documentType: 'DELIVERY_NOTE' })
    );
  }

  override async load(params: {
    query: Query<DeliveryNoteQueryRow>;
  }): Promise<QueryResult<DeliveryNoteQueryRow>> {
    const result = await super.load({ query: { ...params.query, filters: statusFilterAsCodes(params.query.filters) } });
    if (!isRowPageQuery(params.query)) return result;
    if (!isChildPageQuery(params.query)) this.total.set((result as { lastRow?: number }).lastRow ?? 0);
    const ids = (result?.data ?? [])
      .map((row) => row.delivery_note?.id)
      .filter((id): id is string => typeof id === 'string' && id.length > 0);
    if (ids.length > 0) {
      void this.iconsService.loadFor(ids);
      void this.stagesService.loadFor(ids);
    }
    return result;
  }
}
